import type { AxiosError } from 'axios';
import { defineStore } from 'pinia';
import { ref, type Ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { useShowToast } from '@/composables/useShowToast.composable';
import { PurchasedItemTypeEnum } from '@/helpers/enums/financial-system.enum';
import { EnumRoutes } from '@/helpers/enums/routes.enum';
import { handleErrorMessageFromServer } from '@/helpers/functions/handleErrorMessageFromServer';
import type { MoyasarData } from '@/helpers/interfaces/cart-moyasar.interface';
import { MoyasarSchema } from '@/helpers/interfaces/cart-moyasar.interface';
import { type PaymentRedirectData } from '@/helpers/interfaces/financial.interface';
import type { SingleOrder } from '@/helpers/interfaces/order.interface';
import { axiosI, useI18 } from '@/plugins';
import { externalCourses } from '@/router/lms.externalCourses.enum';
import { lms } from '@/router/lms.routes.enum';
import { social } from '@/router/social.routes.enum';
import { useAuthStore } from '@/stores/auth.store';
import { useOrganizationStore } from '@/stores/organzation.pinia';
import { ApiHandler } from '@/submodules/postman-to-ts/apiHandler';
import { dashboardApiHandler } from '@/submodules/postman-to-ts/apiHandler/dashboard';
import { lMSApiHandler } from '@/submodules/postman-to-ts/apiHandler/lMS';
import { socialApiHandler } from '@/submodules/postman-to-ts/social';

interface ICartStore {
  cart: Ref<SingleOrder | null>;
  moyasarData: Ref<MoyasarData | null>;
  isLoading: Ref<boolean>;
  isLoadingTamara: Ref<boolean>;
  isLoadingTabby: Ref<boolean>;
  initializeCartStore: Function;
  removeItemFromCart: (purchasedItemId: string | number) => Promise<void>;
  // @TODO: Function type should never be used

  checkout: Function;
  checkoutAtTamara: Function;
  checkoutAtTabby: Function;
  walletCheckout: Function;
  generateMoyasarCheckout: (useWallet?: boolean) => Promise<
    | MoyasarData
    | {
        message: any;
        status: number;
      }
  >;
  addToCart: ({
    id,
    type,
  }: {
    id: number;
    type: PurchasedItemTypeEnum;
  }) => Promise<boolean | undefined>;
  itemId: Ref<number>;
  applyVoucher: Function;
  removeVoucher: Function;
  verifyPaymentOnRedirect: (
    redirectData: PaymentRedirectData
  ) => Promise<{ redirectTo: string }>;
}

export const useCartStore = defineStore('CartStore', (): ICartStore => {
  /**
   >>>>>>>>>>>>>>> state variables >>>>>>>>>>>>>>>
   */
  const {
    t,
    i18next: { language },
  } = useI18();

  const route = useRoute();
  const router = useRouter();

  const cart = ref<SingleOrder | null>(null);
  const moyasarData = ref<MoyasarData | null>(null);
  const isLoading = ref(false);
  const isLoadingTamara = ref(false);
  const isLoadingTabby = ref(false);
  const itemId = ref(0);
  const authStore = useAuthStore();

  //======================================================================
  //>>>>>>>>>>>>>>> Methods
  //======================================================================
  const initializeCartStore = async (forceRefetch: boolean = false) => {
    if (!authStore.user?.id) return;
    const { path, method } = dashboardApiHandler.financialSystem.users.getCart;
    isLoading.value = true;
    isLoadingTamara.value = true;
    isLoadingTabby.value = true;
    try {
      if (forceRefetch)
        router.replace({
          query: {
            ...route.query,
            forceRefetch: 'true',
          },
        });
      const { data, status } = await axiosI<{
        errors: any[];
        order: SingleOrder;
      }>(path(), {
        method,
      });
      isLoading.value = false;
      isLoadingTamara.value = false;
      isLoadingTabby.value = false;
      if (status == 200 || status == 201) {
        cart.value = data.order;
      }
    } catch (e) {
      console.log('ERROR IN-->initializeCart()', e);
      return e;
    } finally {
      isLoading.value = false;
    }
  };

  const removeItemFromCart = async (purchasedItemId: string | number) => {
    if (!cart.value?.purchasedItems?.length) return;
    const indexOfItemToRemove = cart.value?.purchasedItems.findIndex(
      (item) => item.id == purchasedItemId
    );

    itemId.value =
      (cart.value.purchasedItems[indexOfItemToRemove].itemId as number) || 0;
    const cmsKey =
      'cart.' +
      cart.value.purchasedItems[
        indexOfItemToRemove
      ].purchasedItemType.slug.toLowerCase() +
      'Removed';

    const { path, method } =
      dashboardApiHandler.financialSystem.users.removeItemFromCart;
    await axiosI<{
      errors: any[];
      order: SingleOrder;
    }>(
      path({
        purchasedItemId,
      }),
      {
        method,
      }
    ).catch((e: AxiosError) => {
      return handleErrorMessageFromServer(e);
    });
    itemId.value = 0;
    useShowToast.success({
      msg: t(cmsKey, { courseText: useOrganizationStore().courseText }),
    });
    cart.value.purchasedItems.splice(indexOfItemToRemove, 1);
    initializeCartStore();
  };

  const checkout = async () => {
    isLoading.value = true;
    const { path, method } = dashboardApiHandler.financialSystem.users.checkout;
    try {
      const res = await axiosI<{
        url: string;
        order: SingleOrder;
      }>(path(), {
        method,
      });
      isLoading.value = false;
      return { data: res, status: res.status };
    } catch (e: any) {
      isLoading.value = false;
      return handleErrorMessageFromServer(e);
    }
  };

  const generateMoyasarCheckout = async (useWallet: boolean = false) => {
    isLoading.value = true;

    const { path, method } =
      dashboardApiHandler.financialSystem.users.generateMoyasarCheckout;
    try {
      const res = await axiosI<Omit<MoyasarData, 'language'>>(path(), {
        method,
        params: {
          useWallet,
        },
      });

      const moyasarDataParse = MoyasarSchema.safeParse({
        ...res.data,
        language,
      });
      if (moyasarDataParse.error) {
        if (useOrganizationStore().marketplaceEnabled)
          router.push({
            name: EnumRoutes.financials.singleOrder.name,
            params: {
              orderId: cart.value?.id,
            },
          });
        else {
          const purchasedItem = cart.value?.purchasedItems[0];
          if (!purchasedItem) return { message: res.data, status: res.status };
          let path = '';
          switch (purchasedItem.purchasedItemTypeSlug) {
            case PurchasedItemTypeEnum.COURSE:
              purchasedItem.metadata.startsAt &&
              new Date(purchasedItem.metadata.startsAt).getTime() <
                new Date().getTime()
                ? (path = lms.coursePage.pathFn(purchasedItem.itemId))
                : (path = lms.courseOverview.pathFn(purchasedItem.itemId));
              break;
            case PurchasedItemTypeEnum.EVENT:
              path = social.eventPage.pathFn(purchasedItem.itemId);
              break;
            case PurchasedItemTypeEnum.SPECIALIZATION:
              path = lms.specializationOverview.pathFn(purchasedItem.itemId);
              break;
            case PurchasedItemTypeEnum.EXTERNALCOURSE:
              path = externalCourses.externalCourseOverview.pathFn(
                purchasedItem.itemId!
              );
          }
          if (path) router.push(path);
          return { message: res.data, status: res.status };
        }
        return { message: res.data, status: res.status };
      }
      moyasarData.value = moyasarDataParse.data;

      return moyasarData.value;
    } catch (e: any) {
      console.log({ e });
      return handleErrorMessageFromServer(e);
    } finally {
      isLoading.value = false;
    }
  };

  const checkoutAtTamara = async (useWallet: boolean = false) => {
    isLoadingTamara.value = true;
    const { path, method } =
      dashboardApiHandler.financialSystem.users.checkoutTamara;
    try {
      const res = await axiosI<{
        url: string;
        order: SingleOrder;
      }>(path(), {
        method,
        params: {
          useWallet: useWallet || false,
        },
        headers: {
          language: language,
        },
      });
      isLoadingTamara.value = false;
      return { data: res, status: res.status };
    } catch (e: any) {
      isLoadingTamara.value = false;
      return handleErrorMessageFromServer(e);
    }
  };

  const checkoutAtTabby = async (useWallet: boolean = false) => {
    isLoadingTabby.value = true;
    const { path, method } =
      dashboardApiHandler.financialSystem.users.checkoutTabby;
    try {
      const res = await axiosI<{
        url: string;
        order: SingleOrder;
      }>(path(), {
        method,
        params: {
          useWallet: useWallet || false,
        },
        headers: {
          language: language,
        },
      });
      isLoadingTabby.value = false;
      return { data: res, status: res.status };
    } catch (e: any) {
      isLoadingTabby.value = false;
      return handleErrorMessageFromServer(e);
    }
  };

  const addToCart = async ({
    type,
    id,
  }: {
    id: number;
    type: PurchasedItemTypeEnum;
  }) => {
    let path: string = '';
    let method: string = '';
    itemId.value = id;
    let cmsKey = '';
    let courseText = '';

    switch (type) {
      case PurchasedItemTypeEnum.COURSE:
        path = lMSApiHandler.courses.enrollment.enrollInCourse.path({
          courseId: id,
        });
        method = lMSApiHandler.courses.enrollment.enrollInCourse.method;
        cmsKey = useOrganizationStore().marketplaceEnabled
          ? 'cart.courseAddedSuccessfully'
          : 'cart.coursePurchasedSuccessfully';
        courseText = useOrganizationStore().courseText;
        break;

      case PurchasedItemTypeEnum.EXTERNALCOURSE:
        path = lMSApiHandler.externalCourses.student.enroll.path({
          externalCourseId: id,
        });
        method = lMSApiHandler.externalCourses.student.enroll.method;
        cmsKey = useOrganizationStore().marketplaceEnabled
          ? 'cart.courseAddedSuccessfully'
          : 'cart.coursePurchasedSuccessfully';
        courseText = useOrganizationStore().courseText;
        break;

      case PurchasedItemTypeEnum.EVENT:
        path = socialApiHandler.events.enrollment.enrollInEvent.path({
          eventId: id,
        });
        method = socialApiHandler.events.enrollment.enrollInEvent.method;
        cmsKey = useOrganizationStore().marketplaceEnabled
          ? 'cart.eventAddedSuccessfully'
          : 'cart.eventPurchasedSuccessfully';
        break;

      case PurchasedItemTypeEnum.SPECIALIZATION:
        path = lMSApiHandler.specializations.enrollInSpecialization.path({
          specializationId: id,
        });
        method = lMSApiHandler.specializations.enrollInSpecialization.method;
        cmsKey = useOrganizationStore().marketplaceEnabled
          ? 'cart.specializationAddedSuccessfully'
          : 'cart.specializationPurchasedSuccessfully';
        break;
    }
    isLoading.value = true;
    isLoadingTamara.value = true;
    isLoadingTabby.value = true;
    try {
      const { data } = await axiosI<SingleOrder>(path, {
        method,
      });
      if (data) {
        cart.value = data;
        if (!useOrganizationStore().marketplaceEnabled)
          await generateMoyasarCheckout();
        useShowToast.success({ msg: t(cmsKey, { courseText }) });
        return true;
      }
    } catch (e) {
      handleErrorMessageFromServer(e as AxiosError);
      return false;
    } finally {
      isLoading.value = false;
      isLoadingTamara.value = false;
      isLoadingTabby.value = false;
      itemId.value = 0;
    }
  };

  const applyVoucher = async (voucherCode: string) => {
    const { path, method } =
      dashboardApiHandler.financialSystem.vouchers.applyVoucher;
    try {
      const { data, status } = await axiosI<{
        errors: any[];
        order: SingleOrder;
      }>(path(), {
        method,
        data: {
          voucherCode,
        },
      });
      if (status == 200 || status == 201) {
        cart.value = data.order;
      }
    } catch (e) {
      console.log('ERROR IN-->applyCoupon()', e);
      return e;
    }
  };

  const removeVoucher = async () => {
    const { path, method } =
      dashboardApiHandler.financialSystem.vouchers.removeVoucher;
    try {
      const { data, status } = await axiosI<{
        errors: any[];
        order: SingleOrder;
      }>(path(), {
        method,
      });
      if (status == 200 || status == 201) {
        cart.value = data.order;
      }
    } catch (e) {
      console.log('ERROR IN-->removeVoucher()', e);
      return e;
    }
  };

  const verifyPaymentOnRedirect = async (
    routeData: PaymentRedirectData
  ): Promise<{ redirectTo: string }> => {
    const { path, method } =
      dashboardApiHandler.financialSystem.users.verifyMoyasarPayment;
    const { data } = await axiosI(path(), {
      method,
      data: routeData,
    });

    return data;
  };
  const walletCheckout = async () => {
    isLoading.value = true;
    const { path, method } =
      ApiHandler.dashboard.financialSystem.users.walletCheckout;
    try {
      const res = await axiosI(path(), {
        method,
      });

      router.push({
        name: EnumRoutes.financials.singleOrder.name,
        params: {
          orderId: cart.value?.id,
        },
      });
      return res;
    } catch (e: any) {
      console.log({ e });
      return handleErrorMessageFromServer(e);
    } finally {
      isLoading.value = false;
    }
  };
  return {
    cart,
    isLoading,
    isLoadingTamara,
    isLoadingTabby,
    itemId,
    walletCheckout,
    initializeCartStore,
    addToCart,
    removeItemFromCart,
    checkout,
    checkoutAtTamara,
    checkoutAtTabby,
    applyVoucher,
    removeVoucher,
    generateMoyasarCheckout,
    moyasarData,
    verifyPaymentOnRedirect,
  };
});
